/* eslint-disable @typescript-eslint/no-explicit-any */
import { Route, Routes, useLocation } from 'react-router-dom';
import { ProtectedLayout } from './layout/ProtectedLayout';

import { UnprotectedLayout } from './layout/UnprotectedLayout';
import Login from './pages/login';
import MfaVerify from './pages/login/mfa-verify';
import MfaSetup from './pages/login/mfa-setup';
import MfaReset from './pages/login/mfa-reset';
import MfaResetCode from './pages/login/mfa-reset/code';
import Home from './pages/home';
import AboutPageComponent from './pages/about-page';

// onboarding
import { OnboardingContainer } from './pages/on-boarding/container';
import OnBoardingStep from './pages/on-boarding/step';

import RegistrationForm from './pages/register';
import ResetPassword from './pages/reset-password/code';
import ForgotPassword from './pages/reset-password';

// KNOWLEDGE BASE
import KnowledgeBase from './pages/knowledge-base/page';
import KnowledgeBaseItem from './pages/knowledge-base/knowledge-base-item';

// PODCASTS
import { PodcastListPage } from './pages/podcast/list';
import { PodcastDetailPage } from './pages/podcast/detail';

// RESOURCE HUB
import { ResourceHubListPage } from './pages/resource-hub/list';
import { ResourceHubDetailPage } from './pages/resource-hub/detail';

// KEY DATES
import { KeyDatesListPage } from './pages/key-dates/list';

// Leadership Wheel
import LeadershipWheelPage from './pages/leadership-wheel';

import NotfoundPage from './pages/not-found';
import { BookmarkPageScreen } from './pages/bookmarks';
import NotAuthorizedPage from './pages/not-authorized';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';

import TermsAndConditionsPageComponent from './pages/terms-and-conditions-page';
import PrivacyPolicyPageComponent from './pages/privacy-policy-page';
import CopyrightPageComponent from './pages/copyright-page';
import CookiesPageComponent from './pages/cookies-page';
import { FeedbackPage } from './pages/learn/feedback';
import withAuthGuard, { RoutesEnum } from './auth.guard';

// COURSES

import Courses from './pages/learn/courses';
import CoursesInProgress from './pages/learn/in-progress';
import CoursesCompleted from './pages/learn/completed';
import Course from './pages/learn/course';
import Lesson from './pages/learn/course-lesson';
import { SearchScreen } from './pages/search';
import ProfilePage from './pages/profile';
import { isDevEnvironment } from './hooks/isDevelopment';
import { ManageStaffScreen } from './pages/manage-users';
import { useDocumentTitle } from './hooks/useDocumentTitle';

export function Router() {
  const location = useLocation();

  useDocumentTitle(location.pathname);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    if (isDevEnvironment) {
      console.warn('GA has been disabled because this is a development environment.');
      return;
    }

    if (!import.meta.env.VITE_GA) {
      return;
    }

    ReactGA.initialize(import.meta.env.VITE_GA);
  }, []);

  return (
    <Routes>
      <Route path="/" element={<ProtectedLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutPageComponent />} />

        <Route path="/leadership-wheel" element={<LeadershipWheelPage />} />
        <Route path="/manage-staff" element={withAuthGuard(<ManageStaffScreen />, RoutesEnum.MANAGE_STAFFS)} />

        <Route path="/knowledge-base" element={withAuthGuard(<KnowledgeBase />, RoutesEnum.KNOWLEDGE_BASE)} />
        <Route
          path="/knowledge-base/type/:type"
          element={withAuthGuard(<KnowledgeBase />, RoutesEnum.KNOWLEDGE_BASE)}
        />
        <Route path="/knowledge-base/:slug" element={withAuthGuard(<KnowledgeBaseItem />, RoutesEnum.KNOWLEDGE_BASE)} />

        <Route path="/podcasts" element={withAuthGuard(<PodcastListPage />, RoutesEnum.PODCAST)} />
        <Route path="/podcasts/:slug" element={withAuthGuard(<PodcastDetailPage />, RoutesEnum.PODCAST)} />

        <Route path="/resource-hub" element={withAuthGuard(<ResourceHubListPage />, RoutesEnum.RESOURCE_HUB)} />
        <Route path="/resource/:slug" element={withAuthGuard(<ResourceHubDetailPage />, RoutesEnum.RESOURCE_HUB)} />

        <Route path="/key-dates" element={<KeyDatesListPage />} />

        <Route path="/learn" element={withAuthGuard(<Courses />, RoutesEnum.LEARN)} />
        <Route path="/learn/in-progress" element={withAuthGuard(<CoursesInProgress />, RoutesEnum.LEARN)} />
        <Route path="/learn/completed" element={withAuthGuard(<CoursesCompleted />, RoutesEnum.LEARN)} />
        <Route path="/learn/course/:slug" element={withAuthGuard(<Course />, RoutesEnum.LEARN)} />
        <Route
          path="/learn/course/:slug/module/:idModule/lesson/:idLesson"
          element={withAuthGuard(<Lesson />, RoutesEnum.LEARN)}
        />
        <Route path="/learn/course/:slug/feedback" element={withAuthGuard(<FeedbackPage />, RoutesEnum.LEARN)} />
        <Route path="/search" element={withAuthGuard(<SearchScreen />, RoutesEnum.SEARCH)} />

        <Route path="/profile" element={<ProfilePage />} />

        <Route path="/bookmarks" element={withAuthGuard(<BookmarkPageScreen />, RoutesEnum.BOOKMARK)} />

        <Route element={<OnboardingContainer />}>
          <Route path="/on-boarding/step/:stepNumber" element={<OnBoardingStep />} />
        </Route>

        <Route path="/cookies" element={<CookiesPageComponent />} />
        <Route path="/copyright" element={<CopyrightPageComponent />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPageComponent />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPageComponent />} />
        <Route path="/not-authorized" element={<NotAuthorizedPage />} />
      </Route>
      <Route element={<UnprotectedLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/login/confirmed" element={<Login />} />
        <Route path="/login/mfa" element={<MfaVerify />} />
        <Route path="/login/mfa-setup" element={<MfaSetup />} />
        <Route path="/login/mfa/reset" element={<MfaReset />} />
        <Route path="/login/mfa/reset/code" element={<MfaResetCode />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:code" element={<ResetPassword />} />
        <Route path="/register" element={<RegistrationForm />} />
        <Route path="/register/:code" element={<RegistrationForm />} />
      </Route>
      <Route path="*" element={<NotfoundPage />} />
    </Routes>
  );
}
